import React, { useEffect, useState } from "react";
import { useTranslation } from "react-i18next";
import { X } from "lucide-react";

const STORAGE_KEY = "zv_consent_v2";

const DEFAULT_PREFS = {
  necessary: true,
  functional: true,
  analytics: false,
  ads: false,
  personalization: false,
};

function toConsentSignals(prefs) {
  return {
    ad_storage: prefs.ads ? "granted" : "denied",
    ad_user_data: prefs.ads ? "granted" : "denied",
    ad_personalization: prefs.ads ? "granted" : "denied",
    analytics_storage: prefs.analytics ? "granted" : "denied",
    functionality_storage: prefs.functional ? "granted" : "denied",
    personalization_storage: prefs.personalization ? "granted" : "denied",
    security_storage: "granted",
  };
}

function gtagSafe() {
  if (typeof window === "undefined") return () => {};
  window.dataLayer = window.dataLayer || [];
  return function () {
    window.dataLayer.push(arguments);
  };
}

export function updateConsent(prefs) {
  const gtag = gtagSafe();
  const signals = toConsentSignals(prefs);
  gtag("consent", "update", signals);
  window.dataLayer = window.dataLayer || [];
  window.dataLayer.push({ event: "cookie_consent_update", prefs });
  try {
    localStorage.setItem(STORAGE_KEY, JSON.stringify(signals));
    localStorage.setItem(STORAGE_KEY + "_prefs", JSON.stringify(prefs));
  } catch (e) { /* ignore */ }
}

export default function CookieBanner({ openSignal, onClose }) {
  const { t } = useTranslation();
  const [visible, setVisible] = useState(false);
  const [showPrefs, setShowPrefs] = useState(false);
  const [prefs, setPrefs] = useState(DEFAULT_PREFS);

  useEffect(() => {
    try {
      const saved = localStorage.getItem(STORAGE_KEY);
      if (!saved) {
        setVisible(true);
      } else {
        const p = localStorage.getItem(STORAGE_KEY + "_prefs");
        if (p) setPrefs({ ...DEFAULT_PREFS, ...JSON.parse(p) });
      }
    } catch (e) {
      setVisible(true);
    }
  }, []);

  useEffect(() => {
    if (openSignal) {
      setVisible(true);
      setShowPrefs(true);
    }
  }, [openSignal]);

  const acceptAll = () => {
    const p = { necessary: true, functional: true, analytics: true, ads: true, personalization: true };
    setPrefs(p);
    updateConsent(p);
    close();
  };

  const rejectAll = () => {
    const p = { necessary: true, functional: false, analytics: false, ads: false, personalization: false };
    setPrefs(p);
    updateConsent(p);
    close();
  };

  const savePrefs = () => {
    updateConsent(prefs);
    close();
  };

  const close = () => {
    setVisible(false);
    setShowPrefs(false);
    if (onClose) onClose();
  };

  if (!visible) return null;

  return (
    <div className="fixed inset-x-0 bottom-0 z-50 p-3 md:p-6">
      <div className="max-w-[1180px] mx-auto rounded-2xl border border-[#0d1117] bg-[#0d1117] text-[#f6f0e2] shadow-2xl overflow-hidden">
        {!showPrefs ? (
          <div className="p-5 md:p-8 flex flex-col md:flex-row gap-6 items-start md:items-center">
            <div className="flex-1">
              <div className="zv-mono-tag text-[#c8622a] mb-2">Consent Mode v2</div>
              <h3 className="zv-serif text-2xl md:text-3xl mb-2">{t("cookie.title")}</h3>
              <p className="text-[14px] leading-relaxed text-white/75 max-w-2xl">{t("cookie.body")}</p>
            </div>
            <div className="flex flex-col md:flex-row gap-2 w-full md:w-auto">
              <button onClick={rejectAll} className="zv-btn zv-btn-ghost text-[#f6f0e2] border-[#f6f0e2] hover:bg-[#f6f0e2] hover:text-[#0d1117]">
                {t("cookie.reject_all")}
              </button>
              <button onClick={() => setShowPrefs(true)} className="zv-btn zv-btn-ghost text-[#f6f0e2] border-[#f6f0e2] hover:bg-[#f6f0e2] hover:text-[#0d1117]">
                {t("cookie.manage")}
              </button>
              <button onClick={acceptAll} className="zv-btn zv-btn-cream">
                {t("cookie.accept_all")}
              </button>
            </div>
          </div>
        ) : (
          <div className="p-5 md:p-8">
            <div className="flex items-start justify-between gap-4">
              <div>
                <div className="zv-mono-tag text-[#c8622a] mb-2">Consent Mode v2</div>
                <h3 className="zv-serif text-2xl md:text-3xl mb-2">{t("cookie.prefs_title")}</h3>
                <p className="text-[14px] text-white/75 max-w-2xl">{t("cookie.prefs_lead")}</p>
              </div>
              <button onClick={close} aria-label="Close" className="w-10 h-10 rounded-full border border-white/20 inline-flex items-center justify-center shrink-0">
                <X size={18} />
              </button>
            </div>
            <div className="mt-6 grid grid-cols-1 md:grid-cols-2 gap-3">
              {[
                { k: "necessary", disabled: true },
                { k: "functional", disabled: false },
                { k: "analytics", disabled: false },
                { k: "ads", disabled: false },
                { k: "personalization", disabled: false },
              ].map(({ k, disabled }) => (
                <label
                  key={k}
                  className="flex items-start gap-3 p-4 rounded-xl border border-white/10 hover:border-white/30 transition"
                >
                  <input
                    type="checkbox"
                    checked={prefs[k]}
                    disabled={disabled}
                    onChange={(e) => setPrefs((p) => ({ ...p, [k]: e.target.checked }))}
                    className="mt-1 h-4 w-4 accent-[#c8622a]"
                  />
                  <div>
                    <div className="font-medium">{t(`cookie.cats.${k}.t`)}</div>
                    <div className="text-[13px] text-white/70 mt-1">{t(`cookie.cats.${k}.d`)}</div>
                  </div>
                </label>
              ))}
            </div>
            <div className="mt-6 flex flex-col md:flex-row gap-2 md:justify-end">
              <button onClick={() => setShowPrefs(false)} className="zv-btn zv-btn-ghost text-[#f6f0e2] border-[#f6f0e2] hover:bg-[#f6f0e2] hover:text-[#0d1117]">
                {t("cookie.back")}
              </button>
              <button onClick={rejectAll} className="zv-btn zv-btn-ghost text-[#f6f0e2] border-[#f6f0e2] hover:bg-[#f6f0e2] hover:text-[#0d1117]">
                {t("cookie.reject_all")}
              </button>
              <button onClick={acceptAll} className="zv-btn zv-btn-ghost text-[#f6f0e2] border-[#f6f0e2] hover:bg-[#f6f0e2] hover:text-[#0d1117]">
                {t("cookie.accept_all")}
              </button>
              <button onClick={savePrefs} className="zv-btn zv-btn-cream">
                {t("cookie.save")}
              </button>
            </div>
          </div>
        )}
      </div>
    </div>
  );
}
